import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

/** Android distribution baked in at build time (`VITE_ANDROID_DISTRIBUTION`) and/or native shell. */
export type TAndroidDistribution = 'direct' | 'play'

export interface TNativeAndroidDistributionInfo {
  distribution: TAndroidDistribution
  baked: string | null
  installerPackage: string | null
  fromPlayInstaller: boolean
}

/** Sync Vite-only flag (web bundle). Prefer [resolveAndroidDistribution] for APK OTA policy. */
export function getAndroidDistribution(): TAndroidDistribution {
  const raw = (import.meta.env.VITE_ANDROID_DISTRIBUTION ?? '').trim().toLowerCase()
  return raw === 'play' ? 'play' : 'direct'
}

/** Sync Vite-only check. Prefer [isAndroidPlayStoreDistributionResolved] when async is possible. */
export function isAndroidPlayStoreDistribution(): boolean {
  return getAndroidDistribution() === 'play'
}

/** Cached only after a successful native plugin read (or non-Android). Failed reads must not stick. */
let resolvedNativeCache: TAndroidDistribution | null = null
let resolvedInFlight: Promise<TAndroidDistribution | null> | null = null
/** Bumped by [resetAndroidDistributionResolutionCache] so abandoned in-flight work cannot repopulate cache. */
let resolutionGeneration = 0

/** Test helper: clear native-resolution cache between Vitest cases. */
export function resetAndroidDistributionResolutionCache(): void {
  resolutionGeneration += 1
  resolvedNativeCache = null
  resolvedInFlight = null
}

function parseNativeDistribution(raw: unknown): TNativeAndroidDistributionInfo | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  const distribution = o.distribution === 'play' || o.distribution === 'direct'
    ? o.distribution
    : null
  if (distribution == null) {
    return null
  }
  return {
    distribution,
    baked: typeof o.baked === 'string' ? o.baked : null,
    installerPackage: typeof o.installerPackage === 'string' ? o.installerPackage : null,
    fromPlayInstaller: o.fromPlayInstaller === true
  }
}

/**
 * Resolves Play vs direct from the native shell when available.
 * Native (baked resource + Play installer) wins over Vite so Web OTA cannot re-enable APK OTA (#1241).
 * On Android Tauri, a failed/unparsed native read returns `null` (unknown) — never claim `direct`
 * from the Vite web bundle alone (Web OTA can flip Vite to direct on Play installs).
 * Non-Android / non-Tauri falls back to `VITE_ANDROID_DISTRIBUTION`.
 * Unknown is not cached — a later call can still hit native.
 */
export async function resolveAndroidDistribution(): Promise<TAndroidDistribution | null> {
  if (resolvedNativeCache != null) {
    return resolvedNativeCache
  }
  if (resolvedInFlight != null) {
    return resolvedInFlight
  }
  const generation = resolutionGeneration
  resolvedInFlight = (async () => {
    const vite = getAndroidDistribution()
    if (!isTauriRuntime() || getPlatform() !== 'android') {
      return vite
    }
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const parsed = parseNativeDistribution(
        await invoke<unknown>('plugin:lucimark-android|getAndroidDistribution')
      )
      if (parsed != null) {
        if (generation === resolutionGeneration) {
          resolvedNativeCache = parsed.distribution
        }
        return parsed.distribution
      }
    }
    catch {
      // Plugin bridge may not be ready yet; do not report Vite `direct` as known.
    }
    return null
  })()
  const pending = resolvedInFlight
  try {
    return await pending
  }
  finally {
    if (resolvedInFlight === pending) {
      resolvedInFlight = null
    }
  }
}

/**
 * Whether APK OTA / install-unknown-apps must be skipped.
 * True if Vite says `play`, native resolves to `play`, or Android Tauri native is still
 * unknown (fail closed — avoid installing Lucimark APKs on Play devices).
 */
export async function isAndroidPlayStoreDistributionResolved(): Promise<boolean> {
  if (getAndroidDistribution() === 'play') {
    return true
  }
  const resolved = await resolveAndroidDistribution()
  if (resolved === 'play') {
    return true
  }
  if (resolved == null && isTauriRuntime() && getPlatform() === 'android') {
    return true
  }
  return false
}
